import React, { useCallback, useEffect, useMemo, useState } from "react";
import DashboardLayout from "../../layouts/DashboardLayout";
import { useUserData } from "../../context/UserDataFetcher";
import {
  Alert,
  Box,
  Button,
  Checkbox,
  FormControlLabel,
  Paper,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import { app } from "../../firebaseConfig";
import { getFunctions, httpsCallable } from "firebase/functions";
import { getAuth } from "firebase/auth";
import { formatStatusLabel } from "../../utils/status";
import { ManufacturerRepsManagerPanel } from "./ManageManufacturerReps";

type Scope = "shipments" | "orders" | "items" | "users";

type ShipmentSummary = {
  id: string;
  status?: string;
  manufacturerId?: string;
  dealerId?: string;
  createdAtIso?: string | null;
  shipmentDateIso?: string | null;
  orderCount: number;
  itemCount: number;
  locked?: boolean;
  tags?: string[];
};

type OrderSummary = {
  id: string;
  manufacturerId?: string;
  dealerId?: string;
  shipmentIds?: string[];
  itemCount: number;
  orderNumber?: string;
  status?: string;
  createdAtIso?: string | null;
};

type ItemSummary = {
  id: string;
  orderId?: string;
  manufacturerId?: string;
  shipmentIds?: string[];
  status?: string;
  createdAtIso?: string | null;
};

type UserSummary = {
  id: string;
  email?: string;
  name?: string;
  role?: string;
  manufacturerId?: string;
  dealerId?: string;
  isAdmin?: boolean;
  isDemo?: boolean;
  createdAtIso?: string | null;
};

type DevPanel = "dataTools" | "apiPlayground" | "manufacturerReps";
type ApiAction = "create" | "update" | "delete";

type ApiResult = {
  status?: number;
  body?: string;
  error?: string;
  timestamp?: string;
};

const DeveloperTools: React.FC = () => {
  const userData = useUserData();
  const placeholderManufacturerId = userData?.manufacturerId || "your-manufacturer-id";
  const placeholderOrderId = "MF-102934";
  const placeholderDealerId = userData?.dealerId || "dealer-123";
  const [activeScope, setActiveScope] = useState<Scope>("shipments");
  const [activePanel, setActivePanel] = useState<DevPanel>("dataTools");
  const [shipments, setShipments] = useState<ShipmentSummary[]>([]);
  const [orders, setOrders] = useState<OrderSummary[]>([]);
  const [items, setItems] = useState<ItemSummary[]>([]);
  const [users, setUsers] = useState<UserSummary[]>([]);
  const [selected, setSelected] = useState<Record<string, boolean>>({});
  const [loading, setLoading] = useState(false);

  // Shipment filters
  const [isTest, setIsTest] = useState<boolean>(true);
  const [tag, setTag] = useState<string>("test");

  // Shared filter
  const [createdBefore, setCreatedBefore] = useState<string>("");
  const [manufacturerFilter, setManufacturerFilter] = useState<string>("");

  // Order filters
  const [orderStatusFilter, setOrderStatusFilter] = useState<string>("");

  // Item filters
  const [itemStatusFilter, setItemStatusFilter] = useState<string>("");
  const [itemOrderIdFilter, setItemOrderIdFilter] = useState<string>("");
  const [itemShipmentIdFilter, setItemShipmentIdFilter] = useState<string>("");
  const [userIsDemoFilter, setUserIsDemoFilter] = useState<boolean | "all">("all");
  const defaultFunctionsBase = useMemo(() => {
    const projectId = app?.options?.projectId;
    return projectId ? `https://us-central1-${projectId}.cloudfunctions.net` : "";
  }, []);
  const [apiBaseUrl, setApiBaseUrl] = useState<string>(defaultFunctionsBase);
  const [apiKey, setApiKey] = useState<string>("");
  const [apiSecret, setApiSecret] = useState<string>("");
  const [createPayload, setCreatePayload] = useState<string>(() =>
    JSON.stringify(
      {
        manufacturerId: placeholderManufacturerId,
        dealerId: placeholderDealerId,
        orderNumber: "102934",
        status: "created",
        items: [
          {
            description: "Island Base Cabinet",
            cabinetNumber: "A1",
            barcodeId: "MF-102934-A1",
            shipmentIds: ["ship-7788"],
          },
        ],
      },
      null,
      2
    )
  );
  const [updatePayload, setUpdatePayload] = useState<string>(() =>
    JSON.stringify(
      {
        id: placeholderOrderId,
        manufacturerId: placeholderManufacturerId,
        status: "inTransit",
        items: [
          {
            cabinetNumber: "ZONE-A1",
            barcodeId: `${placeholderOrderId}-A1`,
            shipmentIds: ["ship-9900"],
          },
        ],
        removeItemIds: [`${placeholderOrderId}-old-sample`],
      },
      null,
      2
    )
  );
  const [deletePayload, setDeletePayload] = useState<string>(() =>
    JSON.stringify(
      {
        id: placeholderOrderId,
        manufacturerId: placeholderManufacturerId,
      },
      null,
      2
    )
  );
  const [apiResults, setApiResults] = useState<Record<ApiAction, ApiResult>>({
    create: {},
    update: {},
    delete: {},
  });
  const [apiLoading, setApiLoading] = useState<ApiAction | null>(null);
  const developerTabs: Array<{ id: DevPanel; label: string; helper: string }> = [
    {
      id: "dataTools",
      label: "Data Utilities",
      helper: "Query shipments, orders, and items; unlock or bulk delete.",
    },
    {
      id: "apiPlayground",
      label: "API Coach",
      helper: "Step through POST, PATCH, and DELETE order HTTP calls.",
    },
    {
      id: "manufacturerReps",
      label: "Rep Manager",
      helper: "Preview the manufacturer representative admin UI.",
    },
  ];

  const [authState, setAuthState] = useState<{ checked: boolean; isDevClaim: boolean; isDevDoc: boolean; isAdmin: boolean }>(
    {
      checked: false,
      isDevClaim: false,
      isDevDoc: false,
      isAdmin: false,
    }
  );
  const [authorized, setAuthorized] = useState<boolean>(false);

  const functions = useMemo(() => getFunctions(app, "us-central1"), []);

  useEffect(() => {
    if (userData?.manufacturerId) {
      setManufacturerFilter(userData.manufacturerId);
    }
  }, [userData?.manufacturerId]);

  useEffect(() => {
    setSelected({});
  }, [activeScope]);

  useEffect(() => {
    let cancelled = false;
    const evaluateAccess = async () => {
      const auth = getAuth(app);
      const currentUser = auth.currentUser;
      let isDevClaim = false;
      try {
        if (currentUser) {
          const token = await currentUser.getIdTokenResult(true);
          isDevClaim = token?.claims?.isDev === true;
          console.debug("[DeveloperTools] token claim snapshot", {
            uid: currentUser.uid,
            claims: token?.claims,
            fetchedAt: token?.issuedAtTime,
          });
        } else {
          console.warn("[DeveloperTools] no currentUser when validating access");
        }
      } catch (error) {
        console.error("[DeveloperTools] failed to fetch ID token claims", error);
      }

      const docIsDev = userData?.isDev === true;
      const docIsAdmin = userData?.isAdmin === true;

      const allow = isDevClaim || docIsDev || docIsAdmin;
      if (!cancelled) {
        setAuthState({
          checked: true,
          isDevClaim,
          isDevDoc: docIsDev,
          isAdmin: docIsAdmin,
        });
        setAuthorized(allow);
      }

      console.debug("[DeveloperTools] access evaluation", {
        allow,
        fromClaim: isDevClaim,
        fromDoc: docIsDev,
        fromAdmin: docIsAdmin,
      });
    };

    evaluateAccess();
    return () => {
      cancelled = true;
    };
  }, [userData]);

  const currentList =
    activeScope === "shipments"
      ? shipments
      : activeScope === "orders"
      ? orders
      : activeScope === "items"
      ? items
      : users;

  const toggleAll = useCallback((checked: boolean) => {
    const map: Record<string, boolean> = {};
    const source =
      activeScope === "shipments"
        ? shipments
        : activeScope === "orders"
        ? orders
        : activeScope === "items"
        ? items
        : users;
    source.forEach((entry) => (map[entry.id] = checked));
    setSelected(map);
  }, [activeScope, shipments, orders, items, users]);

  const handleFetch = useCallback(async () => {
    setLoading(true);
    try {
      if (activeScope === "shipments") {
        const listFn = httpsCallable(functions, "devListShipments");
        const res: any = await listFn({
          isTest,
          tag: tag || undefined,
          createdBefore: createdBefore || undefined,
          manufacturerId: userData?.manufacturerId,
          limit: 250,
        });
        const items: ShipmentSummary[] = (res?.data?.shipments || []) as ShipmentSummary[];
        setShipments(items);
      } else if (activeScope === "orders") {
        const listFn = httpsCallable(functions, "devListOrders");
        const status = orderStatusFilter
          ? orderStatusFilter.split(",").map((s) => s.trim()).filter(Boolean)
          : undefined;
        const res: any = await listFn({
          manufacturerId: manufacturerFilter || undefined,
          createdBefore: createdBefore || undefined,
          status,
          limit: 500,
        });
        const items: OrderSummary[] = (res?.data?.orders || []) as OrderSummary[];
        setOrders(items);
      } else if (activeScope === "items") {
        const listFn = httpsCallable(functions, "devListItems");
        const status = itemStatusFilter
          ? itemStatusFilter.split(",").map((s) => s.trim()).filter(Boolean)
          : undefined;
        const res: any = await listFn({
          manufacturerId: manufacturerFilter || undefined,
          orderId: itemOrderIdFilter || undefined,
          shipmentId: itemShipmentIdFilter || undefined,
          createdBefore: createdBefore || undefined,
          status,
          limit: 500,
        });
        const items: ItemSummary[] = (res?.data?.items || []) as ItemSummary[];
        setItems(items);
      } else {
        const listFn = httpsCallable(functions, "devListUsers");
        const res: any = await listFn({
          manufacturerId: manufacturerFilter || undefined,
          isDemo: userIsDemoFilter === "all" ? undefined : userIsDemoFilter,
          limit: 500,
        });
        const items: UserSummary[] = (res?.data?.users || []) as UserSummary[];
        setUsers(items);
      }
      setSelected({});
    } catch (err) {
      console.error("[DeveloperTools] fetch failed", err);
    } finally {
      setLoading(false);
    }
  }, [activeScope, createdBefore, functions, isTest, itemOrderIdFilter, itemShipmentIdFilter, itemStatusFilter, manufacturerFilter, orderStatusFilter, tag, userData?.manufacturerId, userIsDemoFilter]);

  const handleUnlockSelected = useCallback(async () => {
    if (activeScope !== "shipments") return;
    const unlockFn = httpsCallable(functions, "devUnlockShipment");
    const ids = shipments.filter((s) => selected[s.id]).map((s) => s.id);
    for (const id of ids) {
      try {
        await unlockFn({ shipmentId: id });
      } catch (err) {
        console.error("[DeveloperTools] unlock failed", { id, err });
      }
    }
    await handleFetch();
  }, [activeScope, functions, handleFetch, selected, shipments]);

  const handleDeleteSelected = useCallback(async () => {
    if (activeScope === "users") {
      console.warn("[DeveloperTools] delete selected skipped for users scope");
      return;
    }
    const ids = currentList.filter((entry) => selected[entry.id]).map((entry) => entry.id);
    if (!ids.length) return;

    const label = activeScope === "shipments" ? "SHIPMENTS" : activeScope === "orders" ? "ORDERS" : "ITEMS";
    const confirmText = `DELETE ${ids.length} ${label}`;
    const typed = window.prompt(`Type to confirm: ${confirmText}`) || "";
    if (typed.trim() !== confirmText) return;

    try {
      if (activeScope === "shipments") {
        const delFn = httpsCallable(functions, "devBatchDeleteShipments");
        await delFn({ shipmentIds: ids });
      } else if (activeScope === "orders") {
        const delFn = httpsCallable(functions, "devBatchDeleteOrders");
        await delFn({ orderIds: ids });
      } else {
        const delFn = httpsCallable(functions, "devBatchDeleteItems");
        await delFn({ itemIds: ids });
      }
      await handleFetch();
    } catch (err) {
      console.error("[DeveloperTools] delete failed", err);
    }
  }, [activeScope, currentList, functions, handleFetch, selected]);

  const computeSignature = useCallback(async (secret: string, payload: string) => {
    if (typeof window === "undefined" || !window.crypto?.subtle) {
      throw new Error("Web Crypto API is not available in this environment.");
    }
    const encoder = new TextEncoder();
    const cryptoKey = await window.crypto.subtle.importKey(
      "raw",
      encoder.encode(secret),
      { name: "HMAC", hash: "SHA-256" },
      false,
      ["sign"]
    );
    const signatureBuffer = await window.crypto.subtle.sign("HMAC", cryptoKey, encoder.encode(payload));
    const hex = Array.from(new Uint8Array(signatureBuffer))
      .map((value) => value.toString(16).padStart(2, "0"))
      .join("");
    return `sha256=${hex}`;
  }, []);

  const handleApiTest = useCallback(
    async (action: ApiAction) => {
      const trimmedBase = apiBaseUrl.trim().replace(/\/$/, "");
      const trimmedKey = apiKey.trim();
      const trimmedSecret = apiSecret.trim();

      if (!trimmedBase || !trimmedKey || !trimmedSecret) {
        setApiResults((prev) => ({
          ...prev,
          [action]: {
            error: "Base URL, API key, and API secret are required before testing.",
            timestamp: new Date().toISOString(),
          },
        }));
        return;
      }

      const rawPayload =
        action === "create" ? createPayload : action === "update" ? updatePayload : deletePayload;

      if (!rawPayload.trim()) {
        setApiResults((prev) => ({
          ...prev,
          [action]: {
            error: "Payload is empty. Provide a JSON body first.",
            timestamp: new Date().toISOString(),
          },
        }));
        return;
      }

      let normalizedBody: string;
      try {
        const parsed = JSON.parse(rawPayload);
        normalizedBody = JSON.stringify(parsed);
      } catch (err) {
        setApiResults((prev) => ({
          ...prev,
          [action]: {
            error: `Invalid JSON payload: ${err instanceof Error ? err.message : String(err)}`,
            timestamp: new Date().toISOString(),
          },
        }));
        return;
      }

      const endpoint =
        action === "create" ? "createOrder" : action === "update" ? "updateOrder" : "deleteOrder";
      const method = action === "create" ? "POST" : action === "update" ? "PATCH" : "DELETE";

      setApiLoading(action);
      try {
        const signature = await computeSignature(trimmedSecret, normalizedBody);
        const response = await fetch(`${trimmedBase}/${endpoint}`, {
          method,
          headers: {
            "Content-Type": "application/json",
            "X-Api-Key": trimmedKey,
            "X-Signature": signature,
          },
          body: normalizedBody,
        });
        const text = await response.text();
        setApiResults((prev) => ({
          ...prev,
          [action]: {
            status: response.status,
            body: text || "(empty response)",
            timestamp: new Date().toISOString(),
          },
        }));
      } catch (err) {
        setApiResults((prev) => ({
          ...prev,
          [action]: {
            error: err instanceof Error ? err.message : String(err),
            timestamp: new Date().toISOString(),
          },
        }));
      } finally {
        setApiLoading((current) => (current === action ? null : current));
      }
    },
    [apiBaseUrl, apiKey, apiSecret, computeSignature, createPayload, deletePayload, updatePayload]
  );

  const payloadMap: Record<ApiAction, { value: string; setter: (value: string) => void }> = {
    create: { value: createPayload, setter: setCreatePayload },
    update: { value: updatePayload, setter: setUpdatePayload },
    delete: { value: deletePayload, setter: setDeletePayload },
  };

  const apiSections: Array<{
    action: ApiAction;
    title: string;
    method: string;
    summary: string;
    bullets: string[];
    helperText: string;
  }> = [
    {
      action: "create",
      title: "POST /createOrder",
      method: "POST",
      summary: "Creates a fresh order and optional items in one request.",
      bullets: [
        "IDs are derived from your manufacturer prefix + orderNumber.",
        "Any items supplied inherit the order context and auto-generate barcodes.",
        "Ideal for seeding new dealer purchase orders straight from ERP exports.",
      ],
      helperText: "Matches the payload example described in the API Documentation tab.",
    },
    {
      action: "update",
      title: "PATCH /updateOrder",
      method: "PATCH",
      summary: "Applies partial updates to an existing order and item set.",
      bullets: [
        "Only the fields you send mutate; omitted fields remain untouched.",
        "Items are upserted, so cabinet tweaks re-generate barcodes in a single pass.",
        "removeItemIds lets you clean up deprecated sample items instantly.",
      ],
      helperText: "Use this when statuses, shipmentIds, or cabinet numbers change downstream.",
    },
    {
      action: "delete",
      title: "DELETE /deleteOrder",
      method: "DELETE",
      summary: "Removes an order and all of its linked items.",
      bullets: [
        "Provide the canonical order ID (prefix + orderNumber) along with manufacturerId.",
        "All item documents with matching orderId are deleted before the order itself.",
        "Perfect for clearing out bad imports or stale demos.",
      ],
      helperText: "JSON body is easiest here, but the endpoint also accepts query parameters.",
    },
  ];

  if (!authState.checked) {
    return (
      <DashboardLayout>
        <Box>
          <Typography variant="h5">Checking developer access…</Typography>
        </Box>
      </DashboardLayout>
    );
  }

  if (!authorized) {
    return (
      <DashboardLayout>
        <Box>
          <Typography variant="h5">Not authorized</Typography>
          <Typography variant="body1" sx={{ mt: 1 }}>
            Developer access required. If this account was just granted developer access, sign out and sign in again.
          </Typography>
          <Box sx={{ mt: 2, fontFamily: "monospace", fontSize: "0.85rem" }}>
            <div>{`claim.isDev: ${authState.isDevClaim}`}</div>
            <div>{`userDoc.isDev: ${authState.isDevDoc}`}</div>
            <div>{`userDoc.isAdmin: ${authState.isAdmin}`}</div>
          </Box>
        </Box>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout>
      <Box
        sx={{
          display: "flex",
          flexDirection: { xs: "column", md: "row" },
          alignItems: { xs: "stretch", md: "flex-start" },
          gap: 3,
        }}
      >
        <Box component="nav" sx={{ width: { xs: "100%", md: 280 }, flexShrink: 0 }}>
          <Paper variant="outlined" sx={{ p: 2, borderRadius: 2 }}>
            <Typography variant="overline" sx={{ letterSpacing: 1, color: "text.secondary" }}>
              Developer Tabs
            </Typography>
            <Stack spacing={1.5} sx={{ mt: 1 }}>
              {developerTabs.map((tab) => {
                const isActive = activePanel === tab.id;
                return (
                  <Button
                    key={tab.id}
                    fullWidth
                    variant={isActive ? "contained" : "outlined"}
                    color="primary"
                    onClick={() => setActivePanel(tab.id)}
                    sx={{
                      justifyContent: "flex-start",
                      alignItems: "flex-start",
                      textTransform: "none",
                      borderRadius: 2,
                      p: 2,
                    }}
                  >
                    <Box>
                      <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                        {tab.label}
                      </Typography>
                      <Typography
                        variant="caption"
                        sx={{
                          color: isActive ? "primary.contrastText" : "text.secondary",
                          display: "block",
                        }}
                      >
                        {tab.helper}
                      </Typography>
                    </Box>
                  </Button>
                );
              })}
            </Stack>
          </Paper>
        </Box>

        <Box sx={{ flexGrow: 1 }}>
          {activePanel === "dataTools" ? (
            <Stack spacing={3}>
              <Box sx={{ display: "flex", gap: 1.5, flexWrap: "wrap" }}>
                <Button
                  variant={activeScope === "shipments" ? "contained" : "outlined"}
                  onClick={() => setActiveScope("shipments")}
                >
                  Shipments
                </Button>
                <Button
                  variant={activeScope === "orders" ? "contained" : "outlined"}
                  onClick={() => setActiveScope("orders")}
                >
                  Orders
                </Button>
              <Button
                variant={activeScope === "items" ? "contained" : "outlined"}
                onClick={() => setActiveScope("items")}
              >
                Items
              </Button>
              <Button
                variant={activeScope === "users" ? "contained" : "outlined"}
                onClick={() => setActiveScope("users")}
              >
                Users
              </Button>
            </Box>

              {activeScope === "shipments" && (
                <Box sx={{ display: "flex", gap: 2, alignItems: "center", flexWrap: "wrap" }}>
                  <FormControlLabel
                    control={<Checkbox checked={isTest} onChange={(e) => setIsTest(e.target.checked)} />}
                    label="isTest"
                  />
                  <TextField
                    label="Tag contains"
                    value={tag}
                    onChange={(e) => setTag(e.target.value)}
                    size="small"
                  />
                  <TextField
                    label="Created before (ISO)"
                    placeholder="2025-10-01T00:00:00.000Z"
                    value={createdBefore}
                    onChange={(e) => setCreatedBefore(e.target.value)}
                    size="small"
                    sx={{ minWidth: 320 }}
                  />
                  <Button variant="contained" onClick={handleFetch} disabled={loading}>
                    {loading ? "Loading..." : "Fetch"}
                  </Button>
                </Box>
              )}

              {activeScope === "orders" && (
                <Box sx={{ display: "flex", gap: 2, alignItems: "center", flexWrap: "wrap" }}>
                  <TextField
                    label="Manufacturer ID"
                    value={manufacturerFilter}
                    onChange={(e) => setManufacturerFilter(e.target.value)}
                    size="small"
                  />
                  <TextField
                    label="Status filter (comma separated)"
                    value={orderStatusFilter}
                    onChange={(e) => setOrderStatusFilter(e.target.value)}
                    size="small"
                    sx={{ minWidth: 220 }}
                  />
                  <TextField
                    label="Created before (ISO)"
                    placeholder="2025-10-01T00:00:00.000Z"
                    value={createdBefore}
                    onChange={(e) => setCreatedBefore(e.target.value)}
                    size="small"
                    sx={{ minWidth: 320 }}
                  />
                  <Button variant="contained" onClick={handleFetch} disabled={loading}>
                    {loading ? "Loading..." : "Fetch"}
                  </Button>
                </Box>
              )}

              {activeScope === "items" && (
                <Box sx={{ display: "flex", gap: 2, alignItems: "center", flexWrap: "wrap" }}>
                  <TextField
                    label="Manufacturer ID"
                    value={manufacturerFilter}
                    onChange={(e) => setManufacturerFilter(e.target.value)}
                    size="small"
                  />
                  <TextField
                    label="Order ID"
                    value={itemOrderIdFilter}
                    onChange={(e) => setItemOrderIdFilter(e.target.value)}
                    size="small"
                  />
                  <TextField
                    label="Shipment ID"
                    value={itemShipmentIdFilter}
                    onChange={(e) => setItemShipmentIdFilter(e.target.value)}
                    size="small"
                  />
                  <TextField
                    label="Status filter (comma separated)"
                    value={itemStatusFilter}
                    onChange={(e) => setItemStatusFilter(e.target.value)}
                    size="small"
                    sx={{ minWidth: 220 }}
                  />
                  <TextField
                    label="Created before (ISO)"
                    placeholder="2025-10-01T00:00:00.000Z"
                    value={createdBefore}
                    onChange={(e) => setCreatedBefore(e.target.value)}
                    size="small"
                    sx={{ minWidth: 320 }}
                  />
                  <Button variant="contained" onClick={handleFetch} disabled={loading}>
                    {loading ? "Loading..." : "Fetch"}
                  </Button>
                </Box>
              )}

              {activeScope === "users" && (
                <Box sx={{ display: "flex", gap: 2, alignItems: "center", flexWrap: "wrap" }}>
                  <TextField
                    label="Manufacturer ID"
                    value={manufacturerFilter}
                    onChange={(e) => setManufacturerFilter(e.target.value)}
                    size="small"
                  />
                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={userIsDemoFilter === true}
                        indeterminate={userIsDemoFilter === "all"}
                        onChange={(e) => setUserIsDemoFilter(e.target.checked ? true : "all")}
                      />
                    }
                    label="Only show isDemo"
                  />
                  <Button variant="contained" onClick={handleFetch} disabled={loading}>
                    {loading ? "Loading..." : "Fetch"}
                  </Button>
                </Box>
              )}

              {currentList.length > 0 && activeScope !== "users" && (
                <Box sx={{ display: "flex", gap: 2, alignItems: "center", flexWrap: "wrap" }}>
                  <FormControlLabel
                    control={<Checkbox onChange={(e) => toggleAll(e.target.checked)} />}
                    label={`Select all (${currentList.length})`}
                  />
                  {activeScope === "shipments" && (
                    <Button variant="outlined" onClick={handleUnlockSelected}>
                      Unlock Selected
                    </Button>
                  )}
                  <Button color="error" variant="contained" onClick={handleDeleteSelected}>
                    Delete Selected
                  </Button>
                </Box>
              )}

              {activeScope === "shipments" && (
                <Box sx={{ display: "grid", gridTemplateColumns: "1.25rem 20rem 9rem 9rem 9rem 9rem 1fr", rowGap: 1, columnGap: 8 }}>
                  <Typography variant="overline">&nbsp;</Typography>
                  <Typography variant="overline">Shipment ID</Typography>
                  <Typography variant="overline">Orders</Typography>
                  <Typography variant="overline">Items</Typography>
                  <Typography variant="overline">Status</Typography>
                  <Typography variant="overline">Locked</Typography>
                  <Typography variant="overline">Dates</Typography>

                  {shipments.map((s) => (
                    <React.Fragment key={s.id}>
                      <Checkbox
                        checked={!!selected[s.id]}
                        onChange={(e) => setSelected((prev) => ({ ...prev, [s.id]: e.target.checked }))}
                        sx={{ p: 0, alignSelf: "center" }}
                      />
                      <Typography variant="body2" sx={{ fontFamily: "monospace" }}>{s.id}</Typography>
                      <Typography variant="body2">{s.orderCount}</Typography>
                      <Typography variant="body2">{s.itemCount}</Typography>
                      <Typography variant="body2">
                        {formatStatusLabel(s.status) || s.status || "-"}
                      </Typography>
                      <Typography variant="body2">{s.locked ? "yes" : "no"}</Typography>
                      <Typography variant="body2">
                        {s.createdAtIso ? `created ${s.createdAtIso}` : ""}
                        {s.shipmentDateIso ? ` | ship ${s.shipmentDateIso}` : ""}
                      </Typography>
                    </React.Fragment>
                  ))}
                </Box>
              )}

              {activeScope === "orders" && (
                <Box sx={{ display: "grid", gridTemplateColumns: "1.25rem 22rem 10rem 12rem 8rem 1fr", rowGap: 1, columnGap: 8 }}>
                  <Typography variant="overline">&nbsp;</Typography>
                  <Typography variant="overline">Order ID</Typography>
                  <Typography variant="overline">Items</Typography>
                  <Typography variant="overline">Shipments</Typography>
                  <Typography variant="overline">Status</Typography>
                  <Typography variant="overline">Created</Typography>

                  {orders.map((o) => (
                    <React.Fragment key={o.id}>
                      <Checkbox
                        checked={!!selected[o.id]}
                        onChange={(e) => setSelected((prev) => ({ ...prev, [o.id]: e.target.checked }))}
                        sx={{ p: 0, alignSelf: "center" }}
                      />
                      <Typography variant="body2" sx={{ fontFamily: "monospace" }}>{o.id}</Typography>
                      <Typography variant="body2">{o.itemCount}</Typography>
                      <Typography variant="body2">{(o.shipmentIds || []).join(", ") || "-"}</Typography>
                      <Typography variant="body2">
                        {formatStatusLabel(o.status) || o.status || "-"}
                      </Typography>
                      <Typography variant="body2">{o.createdAtIso || "-"}</Typography>
                    </React.Fragment>
                  ))}
                </Box>
              )}

              {activeScope === "items" && (
                <Box sx={{ display: "grid", gridTemplateColumns: "1.25rem 22rem 18rem 12rem 1fr", rowGap: 1, columnGap: 8 }}>
                  <Typography variant="overline">&nbsp;</Typography>
                  <Typography variant="overline">Item ID</Typography>
                  <Typography variant="overline">Order / Shipments</Typography>
                  <Typography variant="overline">Status</Typography>
                  <Typography variant="overline">Created</Typography>

                  {items.map((item) => (
                    <React.Fragment key={item.id}>
                      <Checkbox
                        checked={!!selected[item.id]}
                        onChange={(e) => setSelected((prev) => ({ ...prev, [item.id]: e.target.checked }))}
                        sx={{ p: 0, alignSelf: "center" }}
                      />
                      <Typography variant="body2" sx={{ fontFamily: "monospace" }}>{item.id}</Typography>
                      <Typography variant="body2">
                        {(item.orderId || "-") + (item.shipmentIds?.length ? ` / ${item.shipmentIds.join(", ")}` : "")}
                      </Typography>
                      <Typography variant="body2">
                        {formatStatusLabel(item.status) || item.status || "-"}
                      </Typography>
                      <Typography variant="body2">{item.createdAtIso || "-"}</Typography>
                    </React.Fragment>
                  ))}
                </Box>
              )}

              {activeScope === "users" && (
                <Box sx={{ display: "grid", gridTemplateColumns: "18rem 18rem 8rem 12rem 10rem 1fr", rowGap: 1, columnGap: 8 }}>
                  <Typography variant="overline">User ID</Typography>
                  <Typography variant="overline">Email</Typography>
                  <Typography variant="overline">Role</Typography>
                  <Typography variant="overline">Manufacturer</Typography>
                  <Typography variant="overline">Flags</Typography>
                  <Typography variant="overline">Created</Typography>

                  {users.map((user) => (
                    <React.Fragment key={user.id}>
                      <Typography variant="body2" sx={{ fontFamily: "monospace" }}>{user.id}</Typography>
                      <Typography variant="body2">{user.email || "-"}</Typography>
                      <Typography variant="body2">{user.role || "-"}</Typography>
                      <Typography variant="body2">{user.manufacturerId || "-"}</Typography>
                      <Typography variant="body2">
                        {(user.isAdmin ? "admin " : "") + (user.isDemo ? "demo" : "") || "-"}
                      </Typography>
                      <Typography variant="body2">{user.createdAtIso || "-"}</Typography>
                    </React.Fragment>
                  ))}
                </Box>
              )}
            </Stack>
          ) : activePanel === "apiPlayground" ? (
            <Stack spacing={3}>
              <Box>
                <Typography variant="h4" sx={{ fontWeight: 700 }}>
                  REST API Coach
                </Typography>
                <Typography variant="body1" color="text.secondary">
                  Mirror the POST, PATCH, and DELETE flows from the API Documentation tab and verify responses instantly.
                </Typography>
              </Box>
              <Alert severity="info" variant="outlined">
                API credentials never leave this browser session. They are only used locally to sign the payload before calling your HTTPS endpoint.
              </Alert>
              <Paper variant="outlined" sx={{ p: 3, borderRadius: 2 }}>
                <Stack spacing={2}>
                  <Typography variant="h6" sx={{ fontWeight: 600 }}>
                    Connection settings
                  </Typography>
                  <TextField
                    label="Functions Base URL"
                    value={apiBaseUrl}
                    onChange={(e) => setApiBaseUrl(e.target.value)}
                    helperText="Example: https://us-central1-your-project.cloudfunctions.net"
                  />
                  <TextField
                    label="API Key"
                    value={apiKey}
                    onChange={(e) => setApiKey(e.target.value)}
                    autoComplete="off"
                  />
                  <TextField
                    label="API Secret"
                    type="password"
                    value={apiSecret}
                    onChange={(e) => setApiSecret(e.target.value)}
                    autoComplete="off"
                    helperText="Stored only in memory so you can test signatures safely."
                  />
                </Stack>
              </Paper>
              {apiSections.map((section) => {
                const payloadState = payloadMap[section.action];
                const result = apiResults[section.action];
                const isBusy = apiLoading === section.action;
                return (
                  <Paper key={section.action} variant="outlined" sx={{ p: 3, borderRadius: 2 }}>
                    <Stack spacing={2}>
                      <Box>
                        <Typography variant="h6" sx={{ fontWeight: 600 }}>
                          {section.title}
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                          {section.summary}
                        </Typography>
                        <Box component="ul" sx={{ pl: 3, mt: 1, mb: 0 }}>
                          {section.bullets.map((text) => (
                            <li key={text}>
                              <Typography variant="body2">{text}</Typography>
                            </li>
                          ))}
                        </Box>
                      </Box>
                      <TextField
                        label={`${section.method} payload`}
                        value={payloadState.value}
                        onChange={(e) => payloadState.setter(e.target.value)}
                        multiline
                        minRows={8}
                        helperText={section.helperText}
                      />
                      <Box sx={{ display: "flex", alignItems: "center", gap: 2, flexWrap: "wrap" }}>
                        <Button
                          variant="contained"
                          onClick={() => handleApiTest(section.action)}
                          disabled={isBusy}
                        >
                          {isBusy ? "Testing..." : `Send ${section.method}`}
                        </Button>
                        {result?.timestamp && (
                          <Typography variant="caption" color="text.secondary">
                            Last run: {result.timestamp}
                          </Typography>
                        )}
                      </Box>
                      {result?.error ? (
                        <Box
                          component="pre"
                          sx={{
                            m: 0,
                            p: 2,
                            borderRadius: 2,
                            fontFamily: "monospace",
                            bgcolor: "error.light",
                            color: "error.contrastText",
                            whiteSpace: "pre-wrap",
                          }}
                        >
                          Error: {result.error}
                        </Box>
                      ) : result?.status ? (
                        <Box
                          component="pre"
                          sx={{
                            m: 0,
                            p: 2,
                            borderRadius: 2,
                            fontFamily: "monospace",
                            bgcolor: "background.default",
                            border: (theme) => `1px solid ${theme.palette.divider}`,
                            whiteSpace: "pre-wrap",
                          }}
                        >
                          {`HTTP ${result.status}\n${result.body}`}
                        </Box>
                      ) : (
                        <Typography variant="caption" color="text.secondary">
                          No calls sent yet. Paste a payload above and press the button to try it.
                        </Typography>
                      )}
                    </Stack>
                  </Paper>
                );
              })}
            </Stack>
          ) : (
            <ManufacturerRepsManagerPanel />
          )}
        </Box>
      </Box>
    </DashboardLayout>
  );
};

export default DeveloperTools;
